// @flow
import React, {useEffect, useState} from 'react';
import {
  makeStyles,
  Typography,
  Grid,
  InputAdornment,
  Snackbar,
} from '@material-ui/core';
import {Alert} from '@material-ui/lab';
import {useForm} from 'react-hook-form';
import {useHistory, useLocation} from 'react-router-dom';
import CustomTextField from '../../../../elements/CustomTextField/CustomTextField';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import {colors} from '../../../../assets/styles/colors';
import {
  useCRUDPromotion,
  useFetchPromotions,
  useGetCurrencyCountry,
  useResetState,
} from './utils';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '8px',
    marginBottom: theme.spacing(2),
  },
  contentBackground: {
    backgroundColor: '#fff',
    padding: theme.spacing(2),
    borderRadius: '8px',
  },
  messageSpace: {
    marginBottom: theme.spacing(2, 0),
    alignSelf: 'stretch',
    display: 'flex',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
  },
  messageField: {
    flexGrow: 1,
    marginBottom: theme.spacing(1),
  },
  subTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    color: colors.grey900,
    marginBottom: theme.spacing(2),
  },
  label: {
    fontSize: '1rem',
    color: colors.grey900,
    marginBottom: theme.spacing(1),
  },
  saveButton: {
    marginTop: theme.spacing(2),
    width: 'fit-content',
    paddingInline: theme.spacing(8),
  },
  select: {
    backgroundColor: 'white',
  },
}));

export default function LogisticPromotionsInternational() {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();

  const [isEdit, setIsEdit] = useState(false);
  const [alertOpenError, setAlertOpenError] = useState(false);
  const {register, errors, handleSubmit} = useForm();
  const currency = useGetCurrencyCountry();
  const {promotionMethod} = useFetchPromotions();
  const {resetState} = useResetState();
  const {
    isSaving,
    savingPromotionSuccess,
    savingPromotionError,
    savePromotion,
    updatePromotion,
  } = useCRUDPromotion();

  const handleFormSubmit = data => {
    const {nationalShippingPromotion} = promotionMethod;

    let storeShippingMethodPromotionSetting = {};

    if (nationalShippingPromotion) {
      const {type, national, ...rest} = nationalShippingPromotion;
      storeShippingMethodPromotionSetting = {
        internationalPromotion: {
          ...data,
          enabled: true,
        },
        nationalPromotion: {...rest},
      };
    } else {
      storeShippingMethodPromotionSetting = {
        internationalPromotion: {
          ...data,
          enabled: true,
        },
      };
    }

    if (isEdit) {
      updatePromotion({storeShippingMethodPromotionSetting});
      return;
    }

    savePromotion({storeShippingMethodPromotionSetting});
  };

  useEffect(() => {
    if (savingPromotionSuccess) {
      history.push('/configure/logistics/promotions');
    }
  }, [savingPromotionSuccess, history]);

  useEffect(() => {
    if (savingPromotionError) {
      setAlertOpenError(true);
    }
  }, [savingPromotionError]);

  useEffect(() => {
    if (location.pathname.includes('edit')) {
      setIsEdit(true);
    }
  }, [location.pathname]);

  useEffect(() => {
    return () => {
      resetState();
    };
  }, [resetState]);

  return (
    <div className={classes.root}>
      <Snackbar
        open={alertOpenError}
        autoHideDuration={4000}
        onClose={() => setAlertOpenError(false)}
      >
        <Alert severity="error" onClose={() => setAlertOpenError(false)}>
          Hubo un error al guardar la configuración
        </Alert>
      </Snackbar>

      <div className={classes.contentBackground}>
        <Typography variant="h6" className={classes.subTitle}>
          Define la promoción y términos condiciones
        </Typography>

        {!isEdit && (
          <form onSubmit={handleSubmit(handleFormSubmit)}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <Typography className={classes.label}>
                  A partir de este monto:
                </Typography>
                <CustomTextField
                  className={classes.textField}
                  label="Monto de compra"
                  InputProps={{
                    className: classes.textInput,
                    startAdornment: (
                      <InputAdornment position="start">
                        {currency ? currency.symbol : ''}
                      </InputAdornment>
                    ),
                  }}
                  InputLabelProps={{className: classes.textInputLabel}}
                  name="minOrderAmount"
                  error={!!errors.minOrderAmount}
                  helperText={
                    errors.minOrderAmount ? errors.minOrderAmount.message : ''
                  }
                  inputRef={register({
                    required: 'Campo obligatorio',
                  })}
                  type="number"
                  inputProps={{step: '.01'}}
                  disabled={isSaving}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <Typography className={classes.label}>
                  El costo de envío será:
                </Typography>
                <CustomTextField
                  className={classes.textField}
                  label="Costo de envío"
                  InputProps={{
                    className: classes.textInput,
                    startAdornment: (
                      <InputAdornment position="start">
                        {currency ? currency.symbol : ''}
                      </InputAdornment>
                    ),
                  }}
                  InputLabelProps={{className: classes.textInputLabel}}
                  name="price"
                  error={!!errors.price}
                  helperText={errors.price ? errors.price.message : ''}
                  inputRef={register({
                    required: 'Campo obligatorio',
                  })}
                  type="number"
                  inputProps={{step: '.01'}}
                  disabled={isSaving}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <div className={classes.messageSpace}>
                  <CustomTextField
                    multiline
                    rows={5}
                    rowsMax={18}
                    label="Términos y condiciones"
                    className={`${classes.textField} ${classes.messageField}`}
                    InputProps={{
                      className: classes.textInput,
                    }}
                    InputLabelProps={{className: classes.textInputLabel}}
                    name="tos"
                    error={!!errors.tos}
                    helperText={errors.tos ? errors.tos : ''}
                    type="text"
                    inputRef={register}
                    disabled={isSaving}
                  />
                </div>
              </Grid>
            </Grid>
            <CustomButton
              variant="contained"
              color="primary"
              className={classes.saveButton}
              type="submit"
            >
              Guardar
            </CustomButton>
          </form>
        )}

        {isEdit && promotionMethod && (
          <form onSubmit={handleSubmit(handleFormSubmit)}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <Typography className={classes.label}>
                  A partir de este monto:
                </Typography>
                <CustomTextField
                  className={classes.textField}
                  label="Monto de compra"
                  defaultValue={
                    promotionMethod.internationalShippingPromotion
                      .minOrderAmount
                  }
                  InputProps={{
                    className: classes.textInput,
                    startAdornment: (
                      <InputAdornment position="start">
                        {currency ? currency.symbol : ''}
                      </InputAdornment>
                    ),
                  }}
                  InputLabelProps={{className: classes.textInputLabel}}
                  name="minOrderAmount"
                  error={!!errors.minOrderAmount}
                  helperText={
                    errors.minOrderAmount ? errors.minOrderAmount.message : ''
                  }
                  inputRef={register({
                    required: 'Campo obligatorio',
                  })}
                  type="number"
                  inputProps={{step: '.01'}}
                  disabled={isSaving}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <Typography className={classes.label}>
                  El costo de envío será:
                </Typography>
                <CustomTextField
                  className={classes.textField}
                  label="Costo de envío"
                  InputProps={{
                    className: classes.textInput,
                    startAdornment: (
                      <InputAdornment position="start">
                        {currency ? currency.symbol : ''}
                      </InputAdornment>
                    ),
                  }}
                  defaultValue={
                    promotionMethod.internationalShippingPromotion.price
                  }
                  InputLabelProps={{className: classes.textInputLabel}}
                  name="price"
                  error={!!errors.price}
                  helperText={errors.price ? errors.price.message : ''}
                  inputRef={register({
                    required: 'Campo obligatorio',
                  })}
                  type="number"
                  inputProps={{step: '.01'}}
                  disabled={isSaving}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <div className={classes.messageSpace}>
                  <CustomTextField
                    multiline
                    rows={5}
                    rowsMax={18}
                    label="Términos y condiciones"
                    defaultValue={
                      promotionMethod.internationalShippingPromotion.tos
                    }
                    className={`${classes.textField} ${classes.messageField}`}
                    InputProps={{
                      className: classes.textInput,
                    }}
                    InputLabelProps={{className: classes.textInputLabel}}
                    name="tos"
                    error={!!errors.tos}
                    helperText={errors.tos ? errors.tos : ''}
                    type="text"
                    inputRef={register}
                    disabled={isSaving}
                  />
                </div>
              </Grid>
            </Grid>
            <CustomButton
              variant="contained"
              color="primary"
              className={classes.saveButton}
              type="submit"
            >
              Guardar
            </CustomButton>
          </form>
        )}
      </div>
    </div>
  );
}
